import { adaptLegacySchema } from './legacy-adapter';
import type { ExternalColumnDef, ExternalTableSchema, SubRowConfig, DataSourceDef } from './types';

function resolveColumnDataSource(
  override?: DataSourceDef,
  fromServer?: DataSourceDef,
): DataSourceDef | undefined {
  if (override?.mapper) return override;
  if (fromServer?.mapper) return fromServer;
  return override ?? fromServer;
}

function mergeColumns(
  overrideColumns: ExternalColumnDef[] | undefined,
  adaptedColumns: ExternalColumnDef[] | undefined,
): ExternalColumnDef[] | undefined {
  if (!overrideColumns?.length) return adaptedColumns;
  if (!adaptedColumns?.length) return overrideColumns;

  const adaptedByField = new Map(adaptedColumns.map((column) => [column.field, column]));
  return overrideColumns.map((column) => {
    const fromServer = adaptedByField.get(column.field);
    if (!fromServer) return column;

    const merged: ExternalColumnDef = {
      ...fromServer,
      ...column,
      type: column.type ?? fromServer.type,
      dataSource: resolveColumnDataSource(column.dataSource, fromServer.dataSource),
    };
    if (merged.type !== 'badge') {
      delete merged.badge;
    }
    return merged;
  });
}

function mergeSubRows(
  overrideSubRows: SubRowConfig | undefined,
  adaptedSubRows: SubRowConfig | undefined,
): SubRowConfig | undefined {
  if (overrideSubRows == null) return adaptedSubRows;
  if (adaptedSubRows == null) return overrideSubRows;
  return {
    ...adaptedSubRows,
    ...overrideSubRows,
    columns: overrideSubRows.columns?.length
      ? overrideSubRows.columns
      : adaptedSubRows.columns,
  };
}

/**
 * Merges runtime server schema (V1 shape from useTableSchemaLoader) with static V2 overrides.
 *
 * Override columns win per-field; server-only pieces (apiInjection dataSource, subRowColumns)
 * are preserved when the override omits them or uses placeholder subRow config.
 */
export function mergeServerTableSchema(
  serverSchemaData: Record<string, unknown>,
  overrides: ExternalTableSchema,
): ExternalTableSchema {
  const adapted = adaptLegacySchema(serverSchemaData);
  const overrideSubRows = overrides.features?.rows?.subRows;
  const adaptedSubRows = adapted.features?.rows?.subRows;
  const mergedSubRows = mergeSubRows(overrideSubRows, adaptedSubRows);

  return {
    ...adapted,
    ...overrides,
    metadata: { ...adapted.metadata, ...overrides.metadata },
    dataSource: overrides.dataSource
      ? { ...adapted.dataSource, ...overrides.dataSource }
      : adapted.dataSource,
    columns: mergeColumns(overrides.columns, adapted.columns),
    actions: overrides.actions ?? adapted.actions,
    features: {
      ...adapted.features,
      ...overrides.features,
      rows: {
        ...adapted.features?.rows,
        ...overrides.features?.rows,
        subRows: mergedSubRows,
      },
      groupBy: overrides.features?.groupBy ?? adapted.features?.groupBy,
      editing: overrides.features?.editing ?? adapted.features?.editing,
      pagination: overrides.features?.pagination ?? adapted.features?.pagination,
    },
  };
}
